import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { toast } from "sonner";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowLeft, Code2, Copy, Loader2, QrCode } from "lucide-react";
import { getListing, saveAutoBid, startPayment } from "@/lib/board.functions";
import { BID_STEP, MIN_BID, badgesFor, formatIDR, levelFor } from "@/lib/board-constants";
import { SiteFooter, SiteHeader } from "@/components/site-chrome";
import { ShareListingButton } from "@/components/share-buttons";
import { track, withUtm } from "@/lib/analytics";
import { trackShare } from "@/lib/growth.functions";


export const Route = createFileRoute("/produk/$slug")({
  loader: async ({ params }) => getListing({ data: { slug: params.slug } }),
  head: ({ params, loaderData }) => {
    const listing = loaderData?.listing;
    if (!listing) {
      return {
        meta: [
          { title: "Listing tidak ditemukan — jasasebar.com" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const rank = loaderData?.rank ?? 0;
    const title = `${listing.name} — Peringkat #${rank} ${listing.category} | jasasebar.com`;
    const description =
      listing.description ||
      `Momentum peringkat, riwayat bid, dan statistik klik ${listing.name} di papan promosi jasasebar.com.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: `${listing.name} · peringkat #${rank} di jasasebar.com` },
        { property: "og:description", content: description },
        { property: "og:type", content: "product" },
        { property: "og:url", content: `/produk/${params.slug}` },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: `https://www.jasasebar.com/api/public/og/${params.slug}.png` },
        { name: "twitter:image", content: `https://www.jasasebar.com/api/public/og/${params.slug}.png` },
      ],
      links: [{ rel: "canonical", href: `/produk/${params.slug}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: listing.name,
            description,
            category: listing.category,
            url: listing.url,
            offers: {
              "@type": "Offer",
              price: Math.max(listing.total_amount, 0),
              priceCurrency: "IDR",
              availability: "https://schema.org/InStock",
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Papan", item: "/" },
              { "@type": "ListItem", position: 2, name: listing.name, item: `/produk/${params.slug}` },
            ],
          }),
        },
      ],
    };
  },
  component: ProductPage,
});


function ProductPage() {
  const { slug } = Route.useParams();
  const listingFn = useServerFn(getListing);
  const payFn = useServerFn(startPayment);
  const autoBidFn = useServerFn(saveAutoBid);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["listing", slug],
    queryFn: () => listingFn({ data: { slug } }),
  });

  const [token, setToken] = useState<string | null>(null);
  const [maxBudget, setMaxBudget] = useState(0);

  useEffect(() => {
    setToken(localStorage.getItem(`jasasebar:${slug}`));
  }, [slug]);

  useEffect(() => {
    if (data?.listing) setMaxBudget(data.listing.auto_bid_max);
  }, [data?.listing]);

  const pay = useMutation({
    mutationFn: (amount: number) =>
      payFn({ data: { slug, amount, kind: "bid" as const, origin: window.location.origin } }),
    onSuccess: (res) => {
      window.location.href = res.paymentUrl;
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const autoBid = useMutation({
    mutationFn: () => autoBidFn({ data: { slug, token: token!, max: maxBudget } }),
    onSuccess: () => {
      toast.success("Auto-bid tersimpan. Sistem akan menyalip otomatis dalam batas anggaran.");
      refetch();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <SiteHeader />
        <div className="mx-auto flex max-w-6xl items-center gap-2 px-4 py-24 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Memuat…
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen">
        <SiteHeader />
        <div className="mx-auto max-w-6xl px-4 py-24">
          <h1 className="font-display text-2xl font-semibold">Listing tidak ditemukan</h1>
          <Link to="/" className="mt-3 inline-block text-sm text-primary">
            Kembali ke papan
          </Link>
        </div>
      </div>
    );
  }

  const { listing, history, clicks, rank } = data;
  const badges = badgesFor(listing);
  const level = levelFor(listing.points);
  const nextPrice = Math.max(MIN_BID, listing.total_amount + BID_STEP);
  const chartData = history.map((h) => ({
    label: new Date(h.created_at).toLocaleDateString("id-ID", { day: "2-digit", month: "short" }),
    total: h.total_after,
  }));
  const clickData = clicks.map((c) => ({
    label: new Date(c.day).toLocaleDateString("id-ID", { day: "2-digit", month: "short" }),
    total: c.count,
  }));
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(listing.url)}`;

  return (
    <div className="min-h-screen">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-4 py-10">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="size-4" /> Papan
        </Link>

        <motion.header
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="surface-card mt-4 p-6"
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <span className="text-xs text-muted-foreground">Peringkat saat ini</span>
              <h1 className="font-display text-3xl font-semibold">
                #{rank} · {listing.name}
              </h1>
              <p className="mt-2 max-w-xl text-sm text-muted-foreground">{listing.description}</p>
              <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                <span className="rounded-full bg-secondary px-2 py-0.5">{listing.category}</span>
                <span className="rounded-full bg-secondary px-2 py-0.5">
                  Lv{level.level} · {level.label}
                </span>
                {badges.map((b) => (
                  <span key={b} className="rounded-full bg-primary-soft px-2 py-0.5 text-accent-foreground">
                    {b}
                  </span>
                ))}
              </div>
            </div>
            <div className="text-right">
              <div className="font-display text-2xl font-semibold tabular-nums">
                {listing.total_amount > 0 ? formatIDR(listing.total_amount) : "GRATIS"}
              </div>
              <div className="mt-3 flex items-center justify-end gap-2">
                <ShareListingButton slug={listing.slug} name={listing.name} rank={rank} variant="outline" />
                <button
                  onClick={() => pay.mutate(nextPrice)}
                  disabled={pay.isPending}
                  className="inline-flex h-11 items-center gap-2 rounded-xl bg-primary px-5 text-sm font-medium text-primary-foreground disabled:opacity-50"
                >
                  {pay.isPending && <Loader2 className="size-4 animate-spin" />}
                  Naikkan ke {formatIDR(nextPrice)}
                </button>
              </div>
            </div>

          </div>
        </motion.header>

        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <ChartCard title="Momentum peringkat" data={chartData} color="var(--color-chart-1)" empty="Belum ada bid berbayar." />
          <ChartCard title="Klik harian" data={clickData} color="var(--color-chart-3)" empty="Belum ada klik tercatat." />
        </div>

        <div className="mt-6 grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
          <motion.section
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="surface-card p-6"
          >
            <h2 className="font-display text-lg font-semibold">Auto-bid</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Tentukan anggaran maksimum. Saat ada yang menyalip, sistem otomatis mengembalikan posisi Anda selama
              masih di bawah batas.
            </p>
            {token ? (
              <div className="mt-4 flex flex-wrap gap-3">
                <div className="flex h-11 items-center gap-1 rounded-xl border border-input bg-background px-3">
                  <span className="text-sm text-muted-foreground">Rp</span>
                  <input
                    type="number"
                    min={0}
                    step={BID_STEP}
                    value={maxBudget}
                    onChange={(e) => setMaxBudget(Number(e.target.value))}
                    className="w-40 bg-transparent text-sm outline-none"
                  />
                </div>
                <button
                  onClick={() => autoBid.mutate()}
                  disabled={autoBid.isPending}
                  className="h-11 rounded-xl bg-foreground px-5 text-sm font-medium text-background disabled:opacity-50"
                >
                  Simpan batas
                </button>
              </div>
            ) : (
              <p className="mt-4 rounded-xl bg-secondary p-3 text-sm text-muted-foreground">
                Auto-bid hanya bisa diatur dari perangkat yang mendaftarkan listing ini.
              </p>
            )}
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="surface-card flex flex-col items-center p-6 text-center"
          >
            <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
              <QrCode className="size-4 text-primary" /> QR share
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">Cetak dan sebar offline — klik tetap terhitung.</p>
            <img
              src={qrSrc}
              alt={`Kode QR menuju ${listing.name}`}
              width={160}
              height={160}
              loading="lazy"
              className="mt-4 rounded-xl border border-border"
            />
            <span className="mt-3 text-xs text-muted-foreground">
              {listing.clicks} klik · {listing.points} poin
            </span>
          </motion.section>
        </div>

        <EmbedWidgetCard slug={listing.slug} name={listing.name} />
      </main>
      <SiteFooter />
    </div>
  );
}

function EmbedWidgetCard({ slug, name }: { slug: string; name: string }) {
  const [origin, setOrigin] = useState("");
  const logWidget = useServerFn(trackShare);
  useEffect(() => setOrigin(window.location.origin), []);
  const badge = `${origin}/api/public/widget/${slug}`;
  const target = origin
    ? withUtm(`${origin}/produk/${slug}`, {
        source: "widget",
        medium: "embed",
        campaign: "listing_widget",
        content: slug,
      })
    : "";
  const snippet = `<a href="${target}" target="_blank" rel="noopener">
  <img src="${badge}" width="320" height="90" alt="Peringkat ${name} di jasasebar.com" />
</a>`;

  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="surface-card mt-6 p-6"
    >
      <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
        <Code2 className="size-4 text-primary" /> Widget peringkat
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Pasang badge ini di website atau blog Anda. Badge selalu menampilkan peringkat terbaru dan mengarah kembali ke
        halaman listing.
      </p>
      {origin && (
        <img src={badge} width={320} height={90} alt={`Badge peringkat ${name}`} className="mt-4 rounded-xl" />
      )}
      <textarea
        readOnly
        value={snippet}
        rows={4}
        className="mt-4 w-full rounded-xl border border-input bg-background p-3 font-mono text-xs outline-none"
      />
      <button
        onClick={() => {
          navigator.clipboard.writeText(snippet);
          track("widget_copy", { slug });
          void logWidget({
            data: { slug, event: "widget_copy", source: "widget", medium: "embed", campaign: "listing_widget" },
          }).catch(() => {});
          toast.success("Kode widget disalin.");
        }}
        className="mt-3 inline-flex h-10 items-center gap-2 rounded-xl border border-border px-4 text-sm font-medium hover:bg-secondary"
      >
        <Copy className="size-4" /> Salin kode
      </button>
    </motion.section>
  );
}

function ChartCard({
  title,
  data,
  color,
  empty,
}: {
  title: string;
  data: { label: string; total: number }[];
  color: string;
  empty: string;
}) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="surface-card p-6"
    >
      <h2 className="font-display text-lg font-semibold">{title}</h2>
      {data.length === 0 ? (
        <p className="mt-6 text-sm text-muted-foreground">{empty}</p>
      ) : (
        <div className="mt-4 h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ left: -20, right: 8, top: 8 }}>
              <defs>
                <linearGradient id={`g-${title}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
              <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={11} />
              <YAxis tickLine={false} axisLine={false} fontSize={11} />
              <Tooltip
                contentStyle={{
                  borderRadius: 12,
                  border: "1px solid var(--color-border)",
                  background: "var(--color-card)",
                  fontSize: 12,
                }}
              />
              <Area type="monotone" dataKey="total" stroke={color} strokeWidth={2} fill={`url(#g-${title})`} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </motion.section>
  );
}
